import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  getCurrentUserRole,
  setCurrentUserRole,
  isAdminRole,
  hasFinancialAccess,
  KEYMADE_USER_ROLE_KEY,
} from '@/utils/userRole';

/**
 * RoleContext
 * --------------------------------
 * One source of truth for the current user's role at runtime. Re-renders
 * subscribers when:
 *   - any caller invokes `updateRole(newRole)` (in-tab)
 *   - another tab writes to localStorage[KEYMADE_USER_ROLE_KEY] (cross-tab)
 *
 * Components: `const { role, isAdmin, isFinancial, updateRole } = useRole();`
 * Defensive fallback: `useRole()` works even if the Provider is missing
 * (e.g. unit tests, a misplaced component) — it just won't be reactive.
 */
const RoleContext = createContext(null);

export function RoleProvider({ children }) {
  const [role, setRole] = useState(() => getCurrentUserRole());

  useEffect(() => {
    const handleRoleChange = (event) => {
      const newRole = event?.detail?.role || getCurrentUserRole();
      setRole(newRole);
    };
    const handleStorage = (event) => {
      if (event.key === KEYMADE_USER_ROLE_KEY) {
        setRole(event.newValue || getCurrentUserRole());
      }
    };
    window.addEventListener('keyMadeRoleChanged', handleRoleChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('keyMadeRoleChanged', handleRoleChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const updateRole = useCallback((newRole) => {
    setCurrentUserRole(newRole);
    // The 'keyMadeRoleChanged' listener above will call setRole.
  }, []);

  const value = {
    role,
    isAdmin: isAdminRole(role),
    isFinancial: hasFinancialAccess(role),
    updateRole,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (ctx) return ctx;
  // Defensive fallback — non-reactive but keeps callers working.
  const role = getCurrentUserRole();
  return {
    role,
    isAdmin: isAdminRole(role),
    isFinancial: hasFinancialAccess(role),
    updateRole: setCurrentUserRole,
  };
}
